/**
 * Catch chunk preload failures that happen *outside* of a `React.lazy`
 * importer call, e.g. `<link rel="modulepreload">` tags injected by Vite for
 * eager route preloading. Vite emits a `vite:preloadError` window event in
 * these cases (see https://vite.dev/guide/build.html#load-error-handling).
 *
 * When we detect one we trigger the same session-guarded one-shot reload that
 * `lazyWithRetry` uses, so the user ends up on the fresh `index.html` /
 * manifest without ever seeing a broken screen.
 *
 * @agentic connections
 */

import { reportError } from "@/app/boot/initializeDatadog";
import { CHUNK_RELOAD_STORAGE_KEY } from "@/utils/ui/lazyWithRetry";

/**
 * Register a `vite:preloadError` listener that triggers a guarded reload.
 * Safe to call multiple times — the listener is only attached once.
 * @agentic connections
 */
export const initializePreloadErrorRecovery = (): void => {
  if (typeof window === "undefined") return;
  const w = window as Window & { __benchmarkPreloadRecoveryInstalled?: true };
  if (w.__benchmarkPreloadRecoveryInstalled) return;
  w.__benchmarkPreloadRecoveryInstalled = true;

  window.addEventListener("vite:preloadError", (event) => {
    let alreadyReloaded = false;
    if (typeof sessionStorage !== "undefined") {
      try {
        alreadyReloaded =
          sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) === "1";
      } catch {
        // Ignore storage access errors.
      }
    }

    const payload = event as Event & { payload?: unknown };

    reportError(payload.payload ?? new Error("vite:preloadError"), {
      source: "initializePreloadErrorRecovery",
      outcome: alreadyReloaded ? "already-reloaded" : "reloading",
    });

    if (alreadyReloaded) return;

    try {
      sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, "1");
    } catch {
      // Ignore storage access errors.
    }

    event.preventDefault();
    window.location.reload();
  });
};
